import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
import { onINP } from "web-vitals/attribution";
/**
 * DOM attribute used to attribute INP reports back to a component. Reuses the
 * existing `data-testid` convention so any element already tagged for Playwright/
 * RTL tests gets INP attribution for free.
 */
const PERF_ID_ATTRIBUTE = "data-testid";
/**
 * Event names eligible for INP attribution. Mirrors what web-vitals' `onINP`
 * considers an "interaction" — click, keyboard, and pointer up/down. Excludes
 * high-volume non-interaction events like `mouseenter`/`mouseleave`/`mousemove`
 * that would otherwise drown out actionable signals in verbose mode.
 */
const INTERACTION_EVENT_NAMES = new Set(["click", "keydown", "keyup", "pointerdown", "pointerup"]);
/**
 * Subscribe to Web Vitals INP reports and forward them to the performance marker
 * pipeline. Two modes, selected by `performanceSettings.enableVerboseInpTracking`:
 *
 *  - **worst mode** (default): `web-vitals` `onINP` — worst interaction per page
 *    session. Low volume; suitable for always-on production telemetry.
 *  - **verbose mode**: raw `PerformanceObserver({type:"event"})` — every
 *    interaction whose duration >= `verboseInpThreshold`. Higher volume; intended
 *    for targeted investigation flights.
 *
 * Both modes only report interactions whose target is tagged with `data-testid`,
 * except worst mode also reports untagged interactions rated worse than "good"
 * so unattributed regressions still surface.
 *
 * Call once per page session, after the config provider is set up.
 */
export function initializeWebVitalsTracking(performanceMarker, configProvider) {
    const settings = configProvider().settings.performanceSettings;
    if (!settings.enableInpTracking)
        return;
    if (settings.enableVerboseInpTracking) {
        initializeVerboseMode(performanceMarker, settings.verboseInpThreshold);
    }
    else {
        initializeWorstMode(performanceMarker);
    }
}
/** Worst-per-session mode via `web-vitals` `onINP`. */
function initializeWorstMode(performanceMarker) {
    onINP((metric) => {
        const a = metric.attribution;
        if (!a.interactionTarget)
            return;
        const target = document.querySelector(a.interactionTarget);
        const perfId = findPerfId(target);
        // Tagged interactions always report; untagged only when slow enough to matter.
        if (!perfId && metric.rating === "good")
            return;
        performanceMarker.mark(MarkerTag.INP, {
            mode: "worst",
            value: String(Math.round(metric.value)),
            rating: metric.rating,
            navigationType: metric.navigationType,
            eventType: a.interactionType,
            eventTarget: a.interactionTarget,
            perfId: perfId ?? a.interactionTarget,
            inputDelay: String(Math.round(a.inputDelay)),
            processingDuration: String(Math.round(a.processingDuration)),
            presentationDelay: String(Math.round(a.presentationDelay)),
        });
    }, 
    // Surface a slow interaction even if the user closes the tab before the
    // standard reporting moment.
    { reportAllChanges: true });
}
/**
 * Verbose mode: raw `PerformanceObserver` stream. Tagged-only above threshold.
 * Note: Chromium clamps `durationThreshold` to >=16ms.
 */
function initializeVerboseMode(performanceMarker, threshold) {
    if (typeof PerformanceObserver === "undefined")
        return;
    const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
            if (entry.duration < threshold)
                continue;
            if (!INTERACTION_EVENT_NAMES.has(entry.name))
                continue;
            const target = entry.target instanceof Element ? entry.target : null;
            const perfId = findPerfId(target);
            if (!perfId)
                continue;
            performanceMarker.mark(MarkerTag.INP, {
                mode: "verbose",
                // interactionId groups raw event entries (e.g. pointerdown + pointerup + click)
                // that belong to the same user interaction — lets Kusto dedup/summarize per interaction.
                interactionId: String(entry.interactionId),
                value: String(Math.round(entry.duration)),
                rating: ratingFromDuration(entry.duration),
                eventType: entry.name,
                perfId,
                inputDelay: String(Math.round(entry.processingStart - entry.startTime)),
                processingDuration: String(Math.round(entry.processingEnd - entry.processingStart)),
                presentationDelay: String(Math.round(entry.startTime + entry.duration - entry.processingEnd)),
            });
        }
    });
    try {
        observer.observe({ type: "event", durationThreshold: Math.max(16, threshold), buffered: true });
    }
    catch {
        // Older browsers may not support `type: "event"`.
    }
}
/** Walk to closest ancestor (or self) carrying `data-testid`. */
function findPerfId(target) {
    return target?.closest(`[${PERF_ID_ATTRIBUTE}]`)?.getAttribute(PERF_ID_ATTRIBUTE) ?? null;
}
/** Web Vitals INP rating thresholds per web.dev/inp. */
function ratingFromDuration(durationMs) {
    if (durationMs <= 200)
        return "good";
    if (durationMs <= 500)
        return "needs-improvement";
    return "poor";
}
